import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  CheckCircle2,
  ShieldCheck,
  Microscope,
  Users,
  Award,
  Leaf,
  Heart,
} from "lucide-react";
import Header from "../components/marpay/Header";
import Footer from "../components/marpay/Footer";
import WhatsAppCTA from "../components/marpay/WhatsAppCTA";

const ABOUT_HERO =
  "https://images.unsplash.com/photo-1766297248027-864589dbd336?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBtZWRpY2FsJTIwbGFib3JhdG9yeSUyMHJlc2VhcmNoZXJ8ZW58MHx8fHwxNzc4NDcwMzk3fDA&ixlib=rb-4.1.0&q=85";

const TEAM_IMG =
  "https://images.pexels.com/photos/4173251/pexels-photo-4173251.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";

const values = [
  {
    icon: ShieldCheck,
    title: "Uncompromising Quality",
    body: "Every batch dual-QC released. WHO-GMP aligned facilities, USP/IP monograph testing, and full pharmacovigilance.",
    accent: "var(--mp-brand)",
  },
  {
    icon: Microscope,
    title: "Clinician-led",
    body: "Molecules co-developed with practising specialists — so the formulation matches real-world prescribing.",
    accent: "var(--mp-magenta)",
  },
  {
    icon: Heart,
    title: "Patient-first Economics",
    body: "Fair, transparent pricing so that life-saving medicines stay accessible across Tier 1 to Tier 3 cities.",
    accent: "var(--mp-gold)",
  },
  {
    icon: Leaf,
    title: "Responsible Manufacturing",
    body: "Energy-efficient plants, solvent recovery, and zero-discharge effluent treatment as a baseline — not a bonus.",
    accent: "var(--mp-brand)",
  },
];

const milestones = [
  {
    year: "2010",
    title: "Idea seeded",
    body: "The founders cross paths at one of India's largest pharma houses and begin sketching the blueprint for a leaner, clinician-led company.",
  },
  {
    year: "2018",
    title: "Foundation",
    body: "Marpay Biotec is formally incorporated in Hyderabad with a focus on Psychiatry and General Medicine.",
  },
  {
    year: "2020",
    title: "Orthopedics range",
    body: "Launch of the calcium and muscle-relaxant portfolio, rapidly adopted by orthopaedic surgeons in Tier 2 cities.",
  },
  {
    year: "2022",
    title: "Neurology expansion",
    body: "Marpay enters the neuropathic-pain and anti-epileptic categories with precision-designed combination molecules.",
  },
  {
    year: "2024",
    title: "1,000+ doctors trusted",
    body: "Crossing the 1,000 active prescribing-doctors mark across India.",
  },
  {
    year: "2026",
    title: "Next decade",
    body: "Scaling distribution, doctor-education programs and exploring select international markets across South-East Asia and Africa.",
  },
];

const leaders = [
  {
    name: "Founder & CEO",
    sub: "15+ years · Strategy & Operations",
    bio: "Two decades of leadership across formulation, regulatory and commercial functions at marquee Indian pharma companies.",
  },
  {
    name: "Co-founder & Head",
    sub: "Formulation Science",
    bio: "PhD in pharmaceutical sciences with a deep track record of developing CNS and neuro-pain formulations.",
  },
  {
    name: "Director · Quality & Manufacturing",
    sub: "GMP Systems & Compliance",
    bio: "Two decades of QA leadership and audit-readiness across WHO-GMP, EU-GMP and US-FDA inspected sites.",
  },
];

const facts = [
  { value: "15+", label: "Years founder experience" },
  { value: "1,200+", label: "Doctors actively prescribing" },
  { value: "50+", label: "Cities reached" },
  { value: "4", label: "Therapeutic specialties" },
  { value: "100%", label: "Batch-wise USP/IP testing" },
  { value: "0", label: "Compromises on quality" },
];

export default function AboutPage() {
  return (
    <main data-testid="about-page" className="bg-[var(--mp-bg)] text-[var(--mp-text)]">
      <Header />

      {/* Hero */}
      <section className="relative pt-36 md:pt-44 pb-20 md:pb-28 crosshair-bg overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-end">
          <div className="lg:col-span-7">
            <div className="overline mb-6">
              <span className="inline-flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-[var(--mp-brand)]" />
                About · Marpay Biotec
              </span>
            </div>
            <motion.h1
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter font-medium leading-[0.98]"
              data-testid="about-headline"
            >
              A startup energy.
              <br />
              <span className="italic font-normal" style={{ color: "var(--mp-magenta)" }}>
                A veteran's discipline.
              </span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="mt-8 text-lg md:text-xl text-[var(--mp-text-2)] leading-relaxed max-w-2xl"
            >
              Marpay Biotec is a new-generation Indian pharmaceutical company founded by
              professionals who spent the last <span className="text-[var(--mp-text)] font-medium">15+ years</span>{" "}
              inside the country's largest pharma houses — leading, building distribution
              networks, and answering to doctors face-to-face.
            </motion.p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/products" className="btn-primary text-sm" data-testid="about-cta-products">
                See our products <ArrowUpRight className="h-4 w-4" />
              </Link>
              <Link to="/#contact" className="btn-ghost text-sm" data-testid="about-cta-contact">
                Talk to our team
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9 }}
              className="relative rounded-3xl overflow-hidden border border-[var(--mp-border)] shadow-[0_30px_80px_-30px_rgba(11,25,54,0.35)] aspect-[4/5]"
            >
              <img src={ABOUT_HERO} alt="Modern medical laboratory" className="w-full h-full object-cover" />
              <div className="absolute top-5 left-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/90 text-[11px] font-mono-display tracking-widest uppercase text-[var(--mp-text)]">
                <span className="h-1.5 w-1.5 rounded-full" style={{ background: "var(--mp-gold)" }} /> About Marpay
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-24 md:py-28 bg-white border-y border-[var(--mp-border)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="overline mb-3">Our Story</div>
            <h2 className="font-display text-3xl sm:text-5xl tracking-tighter font-medium leading-[1.05]">
              We started because Indian pharma
              <br />
              <span style={{ color: "var(--mp-brand)" }}>deserved a reset.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-[var(--mp-text-2)] text-lg leading-relaxed">
            <p>
              For more than a decade and a half, our founders led brands inside India's biggest
              pharmaceutical companies. They watched the industry get bigger — but not necessarily
              better. Doctors had louder marketing but quieter conversations. Patients had more
              packs on shelves, but less clarity about quality.
            </p>
            <p>
              Marpay Biotec is our answer to that gap. A modern, focused pharmaceutical company
              with the rigour of a global manufacturer and the empathy of a family doctor. We
              chose four therapeutic areas — <span className="text-[var(--mp-text)] font-medium">Psychiatry, Orthopedics, Neurology and General Medicine</span> — because
              these are the categories our founders understand to the molecule.
            </p>
            <p>
              We don't believe in over-promising. We believe in over-delivering on the things that
              actually matter to doctors and patients: consistency, predictability and a phone
              call that gets picked up on the second ring.
            </p>
          </div>
        </div>
      </section>

      {/* Mission / Vision */}
      <section className="py-24 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="bg-white border border-[var(--mp-border)] rounded-3xl p-10 relative overflow-hidden"
            data-testid="about-mission-card"
          >
            <div aria-hidden className="absolute -top-20 -right-20 w-72 h-72 rounded-full" style={{ background: "rgba(42,75,156,0.07)", filter: "blur(40px)" }} />
            <div className="overline mb-4" style={{ color: "var(--mp-brand)" }}>Our Mission</div>
            <h3 className="font-display text-3xl md:text-4xl tracking-tighter font-medium leading-tight relative">
              To make evidence-based medicines that doctors trust — and patients can afford —
              across India.
            </h3>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="bg-[var(--mp-dark)] text-white rounded-3xl p-10 relative overflow-hidden"
            data-testid="about-vision-card"
          >
            <div aria-hidden className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full" style={{ background: "rgba(237,177,45,0.18)", filter: "blur(60px)" }} />
            <div className="overline mb-4" style={{ color: "var(--mp-gold)" }}>Our Vision</div>
            <h3 className="font-display text-3xl md:text-4xl tracking-tighter font-medium leading-tight relative">
              To be the most clinician-respected pharmaceutical company born in India this decade.
            </h3>
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 md:py-28 bg-white border-y border-[var(--mp-border)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <div className="overline mb-3">What we stand for</div>
              <h2 className="font-display text-3xl sm:text-5xl tracking-tighter font-medium leading-[1.05]">
                Four values.
                <br />
                <span style={{ color: "var(--mp-magenta)" }}>Zero exceptions.</span>
              </h2>
            </div>
            <p className="md:max-w-sm text-[var(--mp-text-2)] leading-relaxed">
              These aren't posters on the office wall — they're the filters every decision passes
              through, from molecule selection to packaging.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {values.map((v, i) => {
              const Icon = v.icon;
              return (
                <motion.div
                  key={v.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.5, delay: i * 0.07 }}
                  className="rounded-3xl border border-[var(--mp-border)] bg-[var(--mp-bg)] p-8 group hover:-translate-y-1 transition-transform"
                  data-testid={`value-card-${i}`}
                >
                  <div className="flex items-center gap-3 mb-5">
                    <div
                      className="h-11 w-11 rounded-xl flex items-center justify-center"
                      style={{ background: "rgba(42,75,156,0.08)", color: v.accent }}
                    >
                      <Icon className="h-5 w-5" strokeWidth={1.7} />
                    </div>
                    <span className="font-mono-display text-[10px] tracking-[0.22em] uppercase text-[var(--mp-text-2)]">
                      0{i + 1} / 04
                    </span>
                  </div>
                  <h4 className="font-display text-2xl font-medium tracking-tight">{v.title}</h4>
                  <p className="mt-3 text-[var(--mp-text-2)] leading-relaxed">{v.body}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Founders / Team */}
      <section className="py-24 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="rounded-3xl overflow-hidden border border-[var(--mp-border)] aspect-[4/5]">
              <img src={TEAM_IMG} alt="Marpay leadership" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="overline mb-3">Leadership</div>
            <h2 className="font-display text-3xl sm:text-5xl tracking-tighter font-medium leading-[1.05]">
              People who have already
              <br />
              <span style={{ color: "var(--mp-brand)" }}>built the playbook.</span>
            </h2>
            <p className="mt-6 text-[var(--mp-text-2)] leading-relaxed max-w-2xl">
              Marpay's founding team brings together decades of operating experience across
              formulation development, regulatory affairs, manufacturing quality and clinical
              education — built inside India's most reputed pharmaceutical companies.
            </p>

            <div className="mt-10 space-y-5">
              {leaders.map((l, i) => (
                <motion.div
                  key={l.name}
                  initial={{ opacity: 0, x: 12 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.45, delay: i * 0.07 }}
                  className="flex items-start gap-4 p-5 rounded-2xl border border-[var(--mp-border)] bg-white"
                  data-testid={`leader-card-${i}`}
                >
                  <div className="h-11 w-11 rounded-xl flex items-center justify-center text-white flex-shrink-0" style={{ background: "var(--mp-brand)" }}>
                    <Users className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-display text-lg font-medium">{l.name}</div>
                    <div className="text-xs font-mono-display tracking-widest uppercase" style={{ color: "var(--mp-magenta)" }}>
                      {l.sub}
                    </div>
                    <p className="mt-2 text-[var(--mp-text-2)] leading-relaxed">{l.bio}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Manufacturing & Quality */}
      <section className="py-24 md:py-28 bg-[var(--mp-dark)] text-white relative overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.1]"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(255,255,255,0.2) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.2) 1px, transparent 1px)",
            backgroundSize: "64px 64px",
          }}
        />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="overline mb-3" style={{ color: "var(--mp-gold)" }}>Quality & Manufacturing</div>
              <h2 className="font-display text-3xl sm:text-5xl tracking-tighter font-medium leading-[1.05]">
                Built to be
                <br />
                <span style={{ color: "var(--mp-gold)" }}>audit-ready.</span>
                <br />
                Always.
              </h2>
              <p className="mt-6 text-slate-300 leading-relaxed max-w-md">
                Our manufacturing partners are WHO-GMP aligned, Schedule M compliant, and operate
                with full pharmacovigilance protocols — independently audited every quarter.
              </p>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
              {[
                { icon: ShieldCheck, t: "WHO-GMP Aligned", b: "Every batch released under WHO-GMP quality systems with dual QC." },
                { icon: Award, t: "ISO 9001 : 2015", b: "Quality management systems certified and externally audited." },
                { icon: Microscope, t: "USP/IP Tested", b: "Every batch tested to monograph standards before release." },
                { icon: CheckCircle2, t: "Pharmacovigilance", b: "Active adverse-event monitoring and rapid response infrastructure." },
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <div
                    key={i}
                    className="p-7 rounded-2xl bg-white/[0.04] border border-white/10 backdrop-blur-sm hover:bg-white/[0.07] transition-colors"
                    data-testid={`quality-card-${i}`}
                  >
                    <div className="h-10 w-10 rounded-xl bg-white/10 flex items-center justify-center mb-5" style={{ color: "var(--mp-gold)" }}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <h4 className="font-display text-xl font-medium">{item.t}</h4>
                    <p className="mt-2 text-slate-300 leading-relaxed text-sm">{item.b}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Milestones */}
      <section className="py-24 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <div className="overline mb-3">Milestones</div>
              <h2 className="font-display text-3xl sm:text-5xl tracking-tighter font-medium leading-[1.05]">
                A short journey,
                <br />
                <span style={{ color: "var(--mp-brand)" }}>told honestly.</span>
              </h2>
            </div>
          </div>

          <ol className="relative border-l-2 border-[var(--mp-border)] pl-6 md:pl-10 space-y-10">
            {milestones.map((m, i) => (
              <motion.li
                key={m.year}
                initial={{ opacity: 0, x: 16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="relative"
                data-testid={`milestone-${m.year}`}
              >
                <span
                  aria-hidden
                  className="absolute -left-[34px] md:-left-[50px] top-1 h-4 w-4 rounded-full border-4 border-[var(--mp-bg)]"
                  style={{ background: i % 2 === 0 ? "var(--mp-brand)" : "var(--mp-magenta)" }}
                />
                <div className="font-mono-display text-xs tracking-[0.25em] uppercase" style={{ color: "var(--mp-gold)" }}>
                  {m.year}
                </div>
                <h4 className="font-display text-2xl md:text-3xl font-medium mt-1">{m.title}</h4>
                <p className="mt-2 text-[var(--mp-text-2)] leading-relaxed max-w-2xl">{m.body}</p>
              </motion.li>
            ))}
          </ol>
        </div>
      </section>

      {/* Facts strip */}
      <section className="pb-24 md:pb-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-[var(--mp-border)] border border-[var(--mp-border)] rounded-2xl overflow-hidden">
            {facts.map((f, i) => (
              <div key={i} className="bg-white p-6 text-center" data-testid={`fact-${i}`}>
                <div className="font-display text-3xl md:text-4xl font-medium tracking-tighter">{f.value}</div>
                <div className="mt-2 text-xs text-[var(--mp-text-2)]">{f.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24 md:pb-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="rounded-3xl p-10 md:p-16 relative overflow-hidden" style={{ background: "var(--mp-brand)" }}>
            <div aria-hidden className="absolute -top-24 -right-24 w-96 h-96 rounded-full" style={{ background: "rgba(237,177,45,0.25)", filter: "blur(40px)" }} />
            <div aria-hidden className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full" style={{ background: "rgba(175,17,130,0.25)", filter: "blur(40px)" }} />
            <div className="relative grid grid-cols-1 md:grid-cols-12 gap-8 items-end text-white">
              <div className="md:col-span-8">
                <div className="overline mb-3" style={{ color: "var(--mp-gold)" }}>Partner with Marpay</div>
                <h3 className="font-display text-3xl sm:text-5xl tracking-tighter font-medium leading-[1.05]">
                  Let's build the next decade of
                  <br />
                  Indian pharma — together.
                </h3>
              </div>
              <div className="md:col-span-4 flex md:justify-end gap-3">
                <Link to="/#contact" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-[var(--mp-text)] text-sm font-medium hover:bg-[var(--mp-gold)] transition-colors" data-testid="about-bottom-cta">
                  Get in touch <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppCTA />
    </main>
  );
}
